import {
  DataStatus,
  FormField,
  Form,
  FormSubmitHandler,
  ButtonGroup,
  FormButton,
  Separator,
  DialogFooter,
} from 'platform/components';
import {Heading, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  useGetServiceMaterialItemIntegrationClaraInformationQuery,
  usePatchServiceMaterialItemIntegrationClaraInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface EditMaterialItemAdditionalInformationProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
  serviceJobId: string;
  serviceItemId: string;
}

export function EditMaterialItemAdditionalInformation(
  props: EditMaterialItemAdditionalInformationProps
) {
  const {
    data: serviceMaterialItemClaraInformation,
    isError: isServiceMaterialItemClaraInformationError,
    isLoading: isServiceMaterialItemClaraInformationLoading,
  } = useGetServiceMaterialItemIntegrationClaraInformationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
    serviceJobId: props.serviceJobId,
    serviceItemId: props.serviceItemId,
  });
  const [patchServiceMaterialItemIntegrationClaraInformation] =
    usePatchServiceMaterialItemIntegrationClaraInformationMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (data) =>
    await patchServiceMaterialItemIntegrationClaraInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: props.serviceJobId,
      serviceItemId: props.serviceItemId,
      body: data,
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <DataStatus
      isLoading={isServiceMaterialItemClaraInformationLoading}
      isError={isServiceMaterialItemClaraInformationError}
    >
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.materialItem.labels.claraIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.materialItem.labels.claraIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<FormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={formSchema}
        defaultValues={{
          oemPart: serviceMaterialItemClaraInformation?.oemPart,
        }}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="oemPart"
              type="switch"
              label={i18n.t('entity.materialItem.labels.oemPart')}
            />
            <DialogFooter>
              <ButtonGroup align="right">
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.saveChanges')}
                  data-testid={suffixTestId('save', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const formSchema = z.object({
  oemPart: z.boolean(),
});

type FormType = z.infer<typeof formSchema>;
